import type { ArtistRankingItem } from '@/types/rankings';
import { Loader2, User } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';

// Import slick-carousel styles
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

interface AllTimeArtistRankingsProps {
  rankings: ArtistRankingItem[];
  loading: boolean;
  error: string | null;
  isDragging: boolean;
  setIsDragging: (isDragging: boolean) => void;
  isMobile: boolean;
}

const AllTimeArtistRankings: React.FC<AllTimeArtistRankingsProps> = ({
  rankings,
  loading,
  error,
  isDragging,
  setIsDragging,
  isMobile,
}) => {
  const getRankColor = useCallback((index: number) => {
    switch (index) {
      case 0:
        return 'bg-yellow-500'; // Gold
      case 1:
        return 'bg-gray-400'; // Silver
      case 2:
        return 'bg-amber-700'; // Bronze
      default:
        return 'bg-gray-700';
    }
  }, []);

  const renderArtistRankingItem = useCallback(
    (item: ArtistRankingItem, index: number) => {
      const isTopThree = index < 3;
      let itemClasses = 'flex items-center p-3 rounded-lg mb-2 hover:bg-gray-600 transition-colors'; // Base classes, removed cursor-default

      if (isTopThree) {
        itemClasses += ' bg-gray-700'; // Lighter background for top 3
        if (index === 0) itemClasses += ' border-l-4 border-yellow-500';
        else if (index === 1) itemClasses += ' border-l-4 border-gray-400';
        else if (index === 2) itemClasses += ' border-l-4 border-amber-700';
      } else {
        itemClasses += ' bg-gray-800'; // Normal background
      }

      // --- Wrap with Link ---
      return (
        <Link
          key={`${item.artist}-${index}`}
          to={`/artist/${encodeURIComponent(item.artist)}`} // Navigate to artist page
          className={`${itemClasses} cursor-pointer`} // Add cursor-pointer
          onClick={(e) => {
            // Prevent navigation if dragging the slider
            if (isDragging) {
              e.preventDefault();
            }
          }}
        >
          {/* Rank badge */}
          <div
            className={`w-8 h-8 flex items-center justify-center rounded-full mr-4 ${getRankColor(index)} flex-shrink-0`}
          >
            <span className="text-white font-bold">{index + 1}</span>
          </div>
          {/* Artist Name */}
          <div className="flex-1 overflow-hidden">
            <h3 className="text-sm font-medium text-white truncate">{item.artist}</h3>
            {/* No second line for title */}
          </div>
          {/* Count */}
          <div className="flex items-center ml-2">
            <User className="w-3 h-3 text-purple-400 mr-1" /> {/* Use User icon */}
            <span className="text-xs text-purple-300 font-medium">{item.count}</span>
          </div>
        </Link>
      );
    },
    [isDragging, getRankColor]
  );

  // --- Responsive Configuration --- //
  const itemsPerSlide = isMobile ? 10 : 5;
  const numSlidesToShow = isMobile ? 1 : 2;
  const numSlidesToScroll = isMobile ? 1 : 2;

  // --- Dynamically generate slides based on current itemsPerSlide --- //
  const slides = useMemo(() => {
    const generatedSlides = [];
    for (let i = 0; i < rankings.length; i += itemsPerSlide) {
      const slideItems = rankings.slice(i, i + itemsPerSlide);
      if (slideItems.length > 0) {
        generatedSlides.push(
          <div key={`artist-slide-${i / itemsPerSlide}`}>
            <div className="space-y-2 px-2">
              {slideItems.map((item, index) => renderArtistRankingItem(item, i + index))}
            </div>
          </div>
        );
      }
    }
    return generatedSlides;
  }, [rankings, itemsPerSlide, renderArtistRankingItem]);

  // --- Carousel Settings --- //
  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: numSlidesToShow,
    slidesToScroll: numSlidesToScroll,
    arrows: false,
    beforeChange: () => setIsDragging(true), // Can reuse drag state if needed, or use separate one
    afterChange: () => setIsDragging(false),
    appendDots: (dots: React.ReactNode) => (
      <div style={{ position: 'absolute', bottom: '-25px' }}>
        <ul style={{ margin: '0px' }}> {dots} </ul>
      </div>
    ),
    customPaging: (i: number) => (
      <div
        style={{
          width: '10px',
          height: '10px',
          background: 'gray',
          borderRadius: '50%',
          display: 'inline-block',
          margin: '0 5px',
        }}
      />
    ),
  };

  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 sm:p-6 shadow-lg mb-8 mx-2 sm:mx-0">
      <h2 className="text-xl font-bold mb-6 text-white flex items-center gap-2">
        <User className="w-5 h-5 text-purple-400" /> {/* Icon for artists */}
        All-Time Artist Rankings (Top 100)
      </h2>
      {error ? (
        <div className="p-4 bg-red-900/50 border border-red-700 rounded-md text-white">
          <p>{error}</p>
          <button
            type="button"
            onClick={() => {}} // This will be handled by the parent
            className="mt-2 px-3 py-1 bg-red-700 rounded hover:bg-red-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center h-40">
          <Loader2 className="animate-spin rounded-full h-12 w-12 text-purple-500" />
        </div>
      ) : rankings.length === 0 ? (
        <p className="text-gray-400">No artist ranking data available</p>
      ) : (
        <div className="slick-container mb-12">
          <Slider key={isMobile ? 'mobile-artist' : 'desktop-artist'} {...settings}>
            {slides}
          </Slider>
        </div>
      )}
    </div>
  );
};

export default AllTimeArtistRankings;
